import { useState, useMemo } from "react";
import Select from "@/components/base/Select";
import OrderTable from "./components/OrderTable";
import { orders, orderTabs, tabHardCounts } from "@/mocks/orders";
import type { OrderTab } from "@/mocks/orders";

const serviceFilters = ["كل الخدمات", "غسيل سيارات", "تنظيف منازل", "مغاسل ملابس"];
const serviceFilterOptions = serviceFilters.map((s) => ({ value: s, label: s }));

const PER_PAGE = 10;

export default function Orders() {
  const [tab, setTab] = useState<OrderTab>("new");
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState(serviceFilters[0]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [overrides, setOverrides] = useState<Record<string, { tab: OrderTab; assignee: string; statusLabel?: string }>>({});

  const allOrders = useMemo(() => {
    return orders.map((o) => {
      const override = overrides?.[o.id];
      return override
        ? { ...o, tab: override.tab, assignee: override.assignee, statusLabel: override.statusLabel ?? o.statusLabel }
        : o;
    });
  }, [overrides]);

  const countsByTab = useMemo(() => {
    const counts: Record<OrderTab, number> = { new: 0, scheduled: 0, current: 0, completed: 0, cancelled: 0 };
    allOrders.forEach((o) => {
      counts[o.tab] += 1;
    });
    // المكتملة مقصوصة في البيانات التجريبية — نحافظ على العدد الإجمالي الأصلي
    counts.completed = Math.max(counts.completed, tabHardCounts.completed);
    return counts;
  }, [allOrders]);

  const tabTotal = countsByTab[tab];
  const totalPages = Math.max(1, Math.ceil(tabTotal / PER_PAGE));

  const filtered = useMemo(() => {
    return allOrders.filter((o) => {
      const matchTab = o.tab === tab;
      const matchSearch =
        !search.trim() ||
        o.id.toLowerCase().includes(search.trim().toLowerCase()) ||
        o.customer.includes(search.trim());
      const matchService = serviceFilter === "كل الخدمات" || o.serviceType === serviceFilter;
      return matchTab && matchSearch && matchService;
    });
  }, [allOrders, tab, search, serviceFilter]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, page]);

  const handleTabChange = (t: OrderTab) => {
    setTab(t);
    setPage(1);
  };

  const handleAssign = (id: string, assignee: string) => {
    setOverrides((prev) => ({ ...prev, [id]: { tab: "current", assignee, statusLabel: "بانتظار البدء" } }));
    setTab("current");
    setPage(1);
  };

  const startIdx = (page - 1) * PER_PAGE + 1;
  const endIdx = Math.min(page * PER_PAGE, tabTotal);

  return (
    <div>
      <h1 className="text-[28px] font-bold text-ghasla-text-heading">الطلبات</h1>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative w-full sm:w-auto">
          <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ghasla-text-secondary" />
          <input
            className="h-11 w-full rounded-xl bg-[#EFF8F8] pl-3 pr-10 text-sm text-ghasla-text-heading outline-none transition-all placeholder:text-ghasla-text-secondary focus:ring-2 focus:ring-[#00AAB7]/20 sm:w-[280px]"
            placeholder="بحث برقم الطلب أو اسم العميل"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Date range */}
        <div className="flex h-11 items-center gap-2 rounded-xl bg-[#EFF8F8] px-3 text-sm text-ghasla-text-secondary">
          <i className="ri-calendar-line" />
          <input
            type="date"
            className="w-[110px] bg-transparent text-sm text-ghasla-text-heading outline-none"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="من"
          />
          <span className="text-ghasla-text-secondary">—</span>
          <input
            type="date"
            className="w-[110px] bg-transparent text-sm text-ghasla-text-heading outline-none"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="إلى"
          />
        </div>

        {/* Service filter */}
        <Select
          value={serviceFilter}
          onChange={setServiceFilter}
          options={serviceFilterOptions}
          className="w-[180px]"
        />

        {/* Export */}
        <button
          type="button"
          className="flex h-10 cursor-pointer items-center gap-2 rounded-[10px] border-[1.5px] border-[#00AAB7] px-4 text-sm font-medium text-ghasla-text-link transition-colors hover:bg-[#F0FDFD]"
        >
          <i className="ri-download-2-line" />
          تصدير
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-[#E4F5F5] pb-px">
        {orderTabs.map((t) => {
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => handleTabChange(t.key)}
              className={`relative cursor-pointer whitespace-nowrap px-4 pb-3 text-[15px] transition-colors ${
                isActive
                  ? "font-semibold text-ghasla-text-link"
                  : "font-normal text-ghasla-text-secondary hover:text-ghasla-text-heading"
              }`}
            >
              {t.label} ({countsByTab[t.key]})
              {isActive && (
                <span className="absolute inset-x-1 bottom-0 h-[3px] rounded-full bg-gradient-to-l from-[#00AAB7] to-[#33D1DA]" />
              )}
            </button>
          );
        })}
      </div>

      {/* Table card */}
      <div className="mt-5 overflow-hidden rounded-[20px] bg-white shadow-card">
        <OrderTable
          orders={paginated}
          tab={tab}
          onAssign={handleAssign}
        />
      </div>

      {/* Pagination */}
      {tabTotal > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-ghasla-text-secondary">
            عرض {startIdx}-{endIdx} من {tabTotal}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-ghasla-text-secondary transition-colors hover:bg-[#F0FDFD] hover:text-ghasla-text-heading disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                  p === page
                    ? "bg-[#00AAB7] text-white"
                    : "text-ghasla-text-secondary hover:bg-[#F0FDFD] hover:text-ghasla-text-heading"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-ghasla-text-secondary transition-colors hover:bg-[#F0FDFD] hover:text-ghasla-text-heading disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}